import { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { useAppDispatch } from "../../redux/store";
import { setErrorObj } from "../redux/errorReducer";
import { T } from "react-translator-component";
import {
  Badge,
  Box, IconButton, Paper,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TextField,
  Typography,
} from "@mui/material";
import CreateIcon from "@mui/icons-material/Create";
import DeleteIcon from "@mui/icons-material/Delete";
import AdminPageFrame from "../components/AdminPageFrame";
import LoadingModal from "../../components/LoadingModal";
import FetchButton from "../components/FetchButton";
import ReadMore from "../components/ReadMore";
import FormModal from "../components/negativeWord/FormModal";
import DeleteConfirmationDialog from "../components/DeleteConfirmationDialog";
import apiServices from "../services/apiServices";
import { isAdmin } from "../util/util";
import { mngStyles } from "../constants/styles";

export default function NegativeWordList() {
  const dispatch = useAppDispatch();
  const { admin } = useSelector((state: any) => state.adminAuth);

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [negativeWords, setNegativeWords] = useState<any[]>([]);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [lastParams, setLastParams] = useState<any>();
  const [pagination, setPagination] = useState<any>({});
  const [showForm, setShowForm] = useState<boolean>(false);
  const [deleteWord, setDeleteWord] = useState<any>();

  useEffect(() => {
    const params = createParams()
    getNegativeWords(params);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getNegativeWords = async (params: any) => {
    try {
      setIsLoading(true);
      const res = await apiServices.getPagyNegativeWordList(params);
      if (params.page === 1) {
        setNegativeWords(res.negative_words);
      } else {
        setNegativeWords([...negativeWords, ...res.negative_words]);
      }
      setPagination(res.pagination)
      setLastParams(params)
    } catch (error: any) {
      dispatch(setErrorObj(error))
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteWord || !isAdmin(admin)) return false

    try {
      setIsLoading(true);
      await apiServices.deleteNegativeWord(deleteWord.id);
      setDeleteWord(undefined)
      await handleReload()
      return true
    } catch (error: any) {
      dispatch(setErrorObj(error))
      return false
    } finally {
      setIsLoading(false);
    }
  };

  const createParams = (page?: number) => {
    const params: any = {
      term: searchTerm,
      page: page ?? 1,
      per_page: 10
    }

    return params
  }

  const loadMore = () => {
    if (!pagination?.next_page || pagination?.next_page < 2) return

    const params = { ...lastParams, page: pagination.next_page}
    getNegativeWords(params);
  };

  const handleChangeSearchTerm = (value: string) => {
    setSearchTerm(value);
  }

  const handleSearch = async () => {
    const params = createParams()
    await getNegativeWords(params)
    return false
  }

  const handleReload = async () => {
    setShowForm(false)
    await getNegativeWords({...lastParams, page: 1})
  }

  return (
    <AdminPageFrame>
      <LoadingModal visible={isLoading} />

      <TableContainer component={Paper} sx={styles.searchContainer}>
        <div style={styles.searchTitle}>{T("mng_search_area_title")}</div>
        <div style={styles.textFieldAndBtn}>
          <TextField
            placeholder={`${T("mng_search_negative_words_placeholder")}`}
            value={searchTerm}
            onChange={(e) => handleChangeSearchTerm(e.target.value)}
            style={styles.searchInputForm}
          />
          <Box sx={styles.btncontainer}>
            <FetchButton title={T("mng_search")} onSubmit={handleSearch}/>
          </Box>
        </div>
      </TableContainer>

      <TableContainer component={Paper}>
        <Table sx={styles.wordsTable} aria-label="caption table">
          <TableHead>
            <TableRow>
              <TableCell style={{ fontWeight: "700", fontSize: 20 }}>
                {T("mng_negative_word_list")}
              </TableCell>
              <TableCell align="right">
                {isAdmin(admin) && (
                  <IconButton color="inherit" onClick={() => setShowForm(true)}>
                    <Badge color="secondary">
                      <CreateIcon />
                      <Typography>
                        {T("mng_create_negative_word")}
                      </Typography>
                    </Badge>
                  </IconButton>
                )}
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {negativeWords.map((negativeWord: any) => (
              <TableRow sx={styles.wordsTableRow} key={negativeWord.id}>
                <TableCell component="th" scope="row">
                  <Box display="flex" alignItems="center">
                    <Typography variant="subtitle1" style={mngStyles.titletable}>
                      {negativeWord.word}
                    </Typography>
                  </Box>
                </TableCell>

                <TableCell align="right">
                  {isAdmin(admin) && (
                    <IconButton onClick={() => setDeleteWord(negativeWord)}>
                      <DeleteIcon color="error" />
                    </IconButton>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        <ReadMore open={pagination.next_page > 0} sx={styles.paginate} onClick={loadMore} />
      </TableContainer>

      <FormModal
        show={showForm}
        onReload={handleReload}
        onClose={() => setShowForm(false)}
      />

      <DeleteConfirmationDialog
        open={Boolean(deleteWord)}
        message={T('mng_delete_negative_word_confirmation_message')}
        validate={async () => deleteWord !== undefined}
        onSubmit={handleDelete}
        onClose={() => setDeleteWord(undefined)}
      />
    </AdminPageFrame>
  );
}

const styles = {
  tableTitle: {
    fontWeight: 600
  },
  searchContainer: {
    marginBottom: "10px",
    padding: "20px",
  },

  searchTitle: {
    padding: "10px 0",
  },

  searchInputForm: {
    width: "100%",
  },

  btncontainer: {
    padding: 0
  },

  textFieldAndBtn: {
    display: "flex"
  },

  paginate: {
    flexDirection: "row" as const,
    justifyContent: "center",
    alignItems: "center",
    display: "flex",
    width: "100%"
  },

  wordsTable: {
    minWidth: 650
  },

  wordsTableRow: {
    "& .MuiTableCell-root": {
      paddingY: "10px"
    }
  }
}
